import Navbar from "components/navbar";
import { useEffect, useState } from "react";
import Card from "components/card";
import { useNavigate } from "react-router-dom";
import { Button, HStack, IconButton } from "@chakra-ui/react";
import { AiFillBackward, AiFillForward } from "react-icons/ai";
import Loader from "components/loader/loader";
import { t } from "i18next";
import { deleteAppConfig, getAppConfigs } from "services/customAPI";
import { toast } from "react-toastify";
import { DTO } from "shared-lib";
import ConfirmationDialog from "components/common/ConfirmationDialog";
import { useOnboarding } from "hooks/useOnboarding";
import { OnboardingOverlay, OnboardingReplayButton } from "components/onboarding";
import { generalOnboarding } from "components/onboarding/steps";

function General() {
  const navigate = useNavigate();
  const onboarding = useOnboarding(generalOnboarding);
  const [isLoading, setIsLoading] = useState(false);
  const [appConfigs, setAppConfigs] = useState<DTO.AppDto[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<string | null>(null);
  const itemsPerPage = 9;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(appConfigs.length / itemsPerPage);

  useEffect(() => {
    const fetchConfigs = async () => {
      setIsLoading(true);
      try {
        const response = await getAppConfigs();
        if (response?.data) {
          setAppConfigs(response.data);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Erro ao buscar configurações!", error);
        toast.error(t("Error fetching configurations"));
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfigs();
  }, []);

  const handleDelete = async (name: string) => {
    try {
      const handleDeleteConfig = await deleteAppConfig(name);
      setIsLoading(true);
      if (handleDeleteConfig) {
        setIsLoading(false);
        navigate("/admin/default");
      }
    } catch (error) {
      console.error("Erro ao excluir configuração:", error);
      toast.error(t("ErrorDeletingConfig"));
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (configName: string) => {
    setConfigToDelete(configName);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (configToDelete) {
      handleDelete(configToDelete);
    }
    setShowDeleteDialog(false);
    setConfigToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setConfigToDelete(null);
  };
  const startIdx = (currentPage - 1) * itemsPerPage;
  const currentItems = appConfigs.slice(startIdx, startIdx + itemsPerPage);

  return (
    <div>
      <Navbar flag={false} brandText={t("General")} />
      <OnboardingOverlay
        isActive={onboarding.isActive}
        step={onboarding.currentStep}
        currentIndex={onboarding.currentIndex}
        totalSteps={onboarding.totalSteps}
        onNext={onboarding.next}
        onPrevious={onboarding.previous}
        onSkip={onboarding.skip}
      />

      {isLoading ? (
        <Loader />
      ) : (
        <Card extra="w-full mt-4 pb-10 h-full">
          <header className="relative flex items-center justify-between p-6">
            <div className="text-xl font-bold text-white">
              {t("General")}
            </div>
            <OnboardingReplayButton onReplay={onboarding.replay} />
          </header>

          {/* Se não tiver nenhuma configuração */}
          {!appConfigs.length ? (
            <div className="px-10 pb-10">
              <h2 className="mb-4 text-gray-400">{t("NoConfigurationsFound")}</h2>
              <div className="flex justify-end">
                <button
                  className="rounded-lg bg-cyan-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-cyan-700 transition-colors"
                  onClick={() =>
                    navigate("/admin/settings/general/createconfig")
                  }
                >
                  {t("CreateConfiguration")}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Grid de cards paginados */}
              <div id="general-config-list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 px-6 pb-6">
                {currentItems.map((config) => (
                  <div
                    key={config.name}
                    className="flex flex-col justify-between rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-5 shadow-lg hover:border-white/20 transition-all duration-200"
                  >
                    <div>
                      <h2 className="mb-1 text-base font-bold text-white tracking-wide">
                        {config.name}
                      </h2>
                      <p className="mb-2 text-sm text-gray-400">
                        {config.placeHolder}
                      </p>
                      {config.value !== undefined && config.value !== null && (
                        <p className="mb-2 text-sm font-medium text-cyan-400">
                          {t("Valor")}: <span className="text-white">{String(config.value)}</span>
                        </p>
                      )}
                      <span
                        className={`inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full ${config.active
                          ? "bg-green-500/20 text-green-400"
                          : "bg-red-500/20 text-red-400"
                          }`}
                      >
                        {config.active ? t("Ativo") : t("Inativo")}
                      </span>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <button
                        className="flex-1 rounded-lg border border-gray-600 px-3 py-2 text-sm font-medium text-gray-300 hover:border-cyan-500 hover:text-cyan-400 transition-colors"
                        onClick={() =>
                          navigate(
                            `/admin/settings/general/edit/${config.name}`
                          )
                        }
                      >
                        {t("Edit")}
                      </button>
                      <button
                        onClick={() => {
                          handleDeleteClick(config.name);
                        }}
                        className="flex-1 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 transition-colors"
                      >
                        {t("delete")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Controles de páginação */}
              {totalPages > 1 && (
                <HStack justify="center" mb={4}>
                  <IconButton
                    icon={<AiFillBackward />}
                    aria-label="Previous"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    isDisabled={currentPage === 1}
                  />
                  {Array.from({ length: totalPages }, (_, i) => (
                    <Button
                      key={i + 1}
                      size="sm"
                      variant={currentPage === i + 1 ? "solid" : "outline"}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </Button>
                  ))}
                  <IconButton
                    icon={<AiFillForward />}
                    aria-label="Next"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    isDisabled={currentPage === totalPages}
                  />
                </HStack>
              )}

              {/* Botão de criar nova config */}
              <div className="flex justify-end px-6">
                <button
                  id="general-add-btn"
                  className="rounded-lg bg-cyan-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-cyan-700 transition-colors"
                  onClick={() =>
                    navigate("/admin/settings/general/createconfig")
                  }
                >
                  {t("CreateConfiguration")}
                </button>
              </div>
            </>
          )}
        </Card>
      )}

      <ConfirmationDialog
        isOpen={showDeleteDialog}
        title={t("ConfirmDeletion")}
        message={t("AreYouSureYouWantToDelete?")}
        confirmText={t("Delete")}
        cancelText={t("Cancel")}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}

export default General;
